/**
 * §P.3 · Qué descuentos caducan y a quién le sube la cuota.
 *
 * **Existe para que el club se entere antes que la familia.** Esa es la
 * diferencia entre un aviso previo y una llamada enfadada en octubre, y es
 * literalmente la razón por la que el endpoint existe.
 *
 * Lo que se pinta es `increase_cents`, no la fecha:
 *
 * > «Caduca el descuento de Lucía» no dice si hay que llamar a esa casa, y «le
 * > sube de 24,65 € a 29 €» sí.
 *
 * Y la cifra ya viene calculada quitando **solo ese descuento**: una cuota puede
 * tener dos, y decir que sube a la tarifa completa cuando el de hermanos sigue
 * vivo sería asustar al club con un número falso. Aquí no se recalcula nada.
 *
 * `within` son **días a secas**. No `30d`: eso responde `400`, y la guía lo decía
 * mal hasta que se mandó de verdad.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Error as PanelError } from '@/shared/ui/Estados';
import { euros } from '@/shared/ui/formato';
import type { DescuentoQueCaduca } from '@/club/descuentos/tipos';

const VENTANAS: readonly { dias: number; etiqueta: string }[] = [
  { dias: 30, etiqueta: 'Este mes' },
  { dias: 60, etiqueta: 'Dos meses' },
  { dias: 90, etiqueta: 'Un trimestre' },
];

/** «Mañana», «en 12 días». Un «0 días» no se lee como hoy. */
const cuando = (dias: number): string => {
  if (dias <= 0) return 'hoy';
  if (dias === 1) return 'mañana';
  return `en ${dias} días`;
};

export function AvisoDeCaducidad({
  club,
  alAbrirAlumno,
}: {
  club: string;
  alAbrirAlumno: (alumnoId: string) => void;
}) {
  const [dias, setDias] = useState(30);

  const caducan = useQuery({
    queryKey: ['descuentos-caducan', club, dias],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .discounts.expiring.get({ query: { within: dias } });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as DescuentoQueCaduca[];
    },
  });

  if (caducan.isError) {
    return (
      <PanelError
        problema={aProblema(caducan.error)}
        reintentar={() => void caducan.refetch()}
      />
    );
  }

  // Mientras carga no se enseña nada: un hueco que aparece y desaparece encima de
  // la lista mueve la página justo cuando alguien va a pulsar.
  if (!caducan.data) return null;

  const total = caducan.data.reduce((s, d) => s + d.increase_cents, 0);

  return (
    <div
      className={
        // Ámbar solo cuando hay algo que hacer: un color de alerta que sale
        // también cuando no pasa nada deja de significar nada.
        caducan.data.length > 0 ? 'aviso' : 'aviso aviso-calma'
      }
      role="status"
    >
      <div className="titulo-fila">
        <strong>
          {caducan.data.length === 0
            ? 'No caduca ningún descuento próximamente'
            : caducan.data.length === 1
              ? 'Caduca 1 descuento'
              : `Caducan ${caducan.data.length} descuentos`}
        </strong>

        <label className="selector-club">
          <span className="sr">Cuánto tiempo mirar</span>
          <select
            value={dias}
            onChange={(e) => setDias(Number(e.target.value))}
          >
            {VENTANAS.map((v) => (
              <option key={v.dias} value={v.dias}>
                {v.etiqueta}
              </option>
            ))}
          </select>
        </label>
      </div>

      {caducan.data.length === 0 ? (
        <p className="tenue">
          Cuando uno esté a punto de terminar aparecerá aquí, con lo que va a
          subirle a esa familia.
        </p>
      ) : (
        <>
          <ul className="caducan">
            {caducan.data.map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  className="enlace-fila"
                  onClick={() => alAbrirAlumno(d.student_id)}
                >
                  {d.student_name}
                </button>{' '}
                <span className="tenue">
                  {d.name} (<code>{d.code}</code>) termina {cuando(d.days_left)}
                </span>
                {/* La cifra que decide si hay que llamar a esa casa. */}
                <strong className="caducan-subida">
                  {euros(d.current_cents)} → {euros(d.next_cents)}
                </strong>
              </li>
            ))}
          </ul>

          <p className="tenue">
            En total, <strong>{euros(total)} más al mes</strong> para el club —
            y otras tantas familias a las que conviene avisar antes de que les
            llegue el recibo.
          </p>
        </>
      )}
    </div>
  );
}
